import {
    AwardIcon,
    ClockIcon,
    FileTextIcon,
    HandshakeIcon,
    ListIcon,
    ReceiptTextIcon,
    ScaleIcon,
    type SvgIconComponent,
    WrenchIcon,
} from 'components/icons';
import type { IntlShape } from 'react-intl';
import type { AiToolCallHistoryItem, AiToolOutputSummary, PortalChatMessage } from 'types/aiChat';
import { figureTile, shownOfTotal } from '../message/figure/figureMeta';

// The tools the AI reads its own instructions and the page with: no step of their own.
const STEPLESS_TOOLS = new Set(['readSkill', 'readUiContext']);
// The meeting briefs' steps count two things, each its own plural.
const MEETING_PREP_TOOLS = new Set(['generateMeetingPrep', 'generateGroupMeetingPrep']);

/** A turn's steps: one per tool call, in arrival order. */
export const stepsOf = (toolCalls: AiToolCallHistoryItem[]) =>
    toolCalls.filter((toolCall) => !STEPLESS_TOOLS.has(toolCall.name));

const hasMessage = (intl: IntlShape, id: string) => intl.messages[id] !== undefined;

// A Figure's step names its kind ("Laying out the table…"); while it runs a viewer without the input sees no kind.
const figureKind = ({ output, input }: AiToolCallHistoryItem) => {
    const kind = (output as { kind?: unknown } | undefined)?.kind ?? (input as { kind?: unknown } | undefined)?.kind;
    return typeof kind === 'string' ? kind : 'other';
};

/** The tool in plain words, for its card and a step with no label of its own; else its raw name. */
export const toolName = (intl: IntlShape, name: string) => {
    const id = `ai-chat.tool-call.names.${name}`;
    return hasMessage(intl, id) ? intl.formatMessage({ id }) : name;
};

/** What the status line and a running step say: the tool's running label, or "Working on it…" without one. */
export const runningLabel = (intl: IntlShape, step: AiToolCallHistoryItem) => {
    const id = `ai-chat.progress.status.${step.name}`;
    return intl.formatMessage(
        { id: hasMessage(intl, id) ? id : 'ai-chat.progress.working' },
        { kind: figureKind(step) }
    );
};

/** A finished step's past-tense label, else the tool's plain-word name. */
export const doneLabel = (intl: IntlShape, step: AiToolCallHistoryItem) => {
    const id = `ai-chat.progress.done.${step.name}`;
    return hasMessage(intl, id) ? intl.formatMessage({ id }, { kind: figureKind(step) }) : toolName(intl, step.name);
};

/** The rows a finished step returned, with their noun ("14 cases"); none for a tool that counts no rows. */
export const stepChip = (intl: IntlShape, { name, summary }: AiToolCallHistoryItem) => {
    const id = `ai-chat.progress.chip.${name}`;
    if (!summary || !hasMessage(intl, id)) {
        return null;
    }
    if (MEETING_PREP_TOOLS.has(name)) {
        const { cases, bdEfforts } = summary.counts ?? {};
        return typeof cases === 'number' && typeof bdEfforts === 'number'
            ? intl.formatMessage({ id }, { cases, efforts: bdEfforts })
            : null;
    }
    return summary.count === null ? null : intl.formatMessage({ id }, { count: summary.count });
};

/** The partial wording of a cut result, nothing when the rows were not cut or not counted. */
export const partialLabel = (intl: IntlShape, summary: AiToolOutputSummary | undefined) =>
    summary?.partial && summary.count !== null ? shownOfTotal(intl, summary.count, summary.total) : null;

// What an aggregation or a brief adds up, for its card's tile; a search takes its Figure's glyph.
const TOOL_ICON: Partial<Record<string, SvgIconComponent>> = {
    aggregateCases: ScaleIcon,
    aggregateEfforts: HandshakeIcon,
    aggregateEntityEfforts: HandshakeIcon,
    aggregateInvoices: ReceiptTextIcon,
    aggregateEntityInvoices: ReceiptTextIcon,
    aggregateTimecards: ClockIcon,
    aggregateTimecardsWithAmounts: ClockIcon,
    aggregateEntityTimecards: ClockIcon,
    generateMeetingPrep: FileTextIcon,
    generateGroupMeetingPrep: FileTextIcon,
    storeMeetingPrepSummaries: FileTextIcon,
    createExperienceRecordDraft: AwardIcon,
};

/** The glyph on a tool card's tile: what the tool reads, or a wrench for a tool that reads no records. */
export const toolIcon = (name: string): SvgIconComponent => {
    if (TOOL_ICON[name]) {
        return TOOL_ICON[name];
    }
    // A list's own glyph is what a Figure of no known source shows.
    const { icon } = figureTile({ kind: 'list', source: { tool: name } });
    return icon === ListIcon ? WrenchIcon : icon;
};

/**
 * The whole turn in whole seconds: from the question's stored time to the answer's. Null while either is
 * unknown, as for an answer stopped before its finish.
 */
export const turnSeconds = (metadata: PortalChatMessage['metadata']): number | null => {
    const start = Date.parse(metadata?.turnStartedAt ?? '');
    const end = Date.parse(metadata?.turnFinishedAt ?? '');
    return Number.isNaN(start) || Number.isNaN(end) ? null : Math.max(0, Math.round((end - start) / 1000));
};
